import type { AxiosInstance } from 'axios'
import { isAxiosError } from 'axios'

import type { Logger } from '@/lib/logger'
import type {
  RequestErrorInterceptor,
  RequestInterceptor,
  SuccessfulResponseInterceptor,
  ResponseErrorInterceptor,
} from '@/utils/axios'

const onRequest =
  (logger: Logger, serviceName: string): RequestInterceptor =>
  config => {
    logger.debug(
      `[${serviceName}] Requested: ${config.method?.toUpperCase()} ${config.baseURL}${
        config.url
      }`,
    )

    return config
  }

const onRequestError =
  (logger: Logger, serviceName: string): RequestErrorInterceptor =>
  error => {
    logger.error(`[${serviceName}] Requested: ${JSON.stringify(error)}`)

    return Promise.reject(error)
  }

const onSuccessfulResponse =
  (logger: Logger, serviceName: string): SuccessfulResponseInterceptor =>
  response => {
    logger.debug(`[${serviceName}] Received: ${response.status} ${response.statusText}`)

    return response
  }

const onErrorResponse =
  (logger: Logger, serviceName: string): ResponseErrorInterceptor =>
  error => {
    if (isAxiosError(error)) {
      if (!error.response) {
        logger.error(`[${serviceName}] Network error. Did not receive a response.`)

        return Promise.reject(error)
      }

      logger.error(
        `[${serviceName}] Received: ${error.response.status} ${error.response.statusText}`,
      )

      return Promise.reject(error)
    }

    logger.error(`[${serviceName}] An unexpected error occurred.`)

    return Promise.reject(error)
  }

export const axiosLog =
  (logger: Logger, serviceName: string) => (axiosInstance: AxiosInstance) => {
    axiosInstance.interceptors.request.use(
      onRequest(logger, serviceName),
      onRequestError(logger, serviceName),
    )
    axiosInstance.interceptors.response.use(
      onSuccessfulResponse(logger, serviceName),
      onErrorResponse(logger, serviceName),
    )

    return axiosInstance
  }
